const REFERRAL_CODE_STORAGE_KEY = "dataup_signup_referral_code";

export const normalizeReferralCode = (code?: string | null) =>
  String(code || "").trim().toUpperCase().slice(0, 32);

const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

export const getReferralCodeFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  return normalizeReferralCode(params.get("ref"));
};

export const getStoredReferralCode = () => {
  const storage = getSessionStorage();
  try {
    return normalizeReferralCode(storage?.getItem(REFERRAL_CODE_STORAGE_KEY));
  } catch (error) {
    return "";
  }
};

export const storeReferralCode = (code?: string | null) => {
  const normalizedCode = normalizeReferralCode(code);
  const storage = getSessionStorage();

  try {
    if (normalizedCode) {
      storage?.setItem(REFERRAL_CODE_STORAGE_KEY, normalizedCode);
    } else {
      storage?.removeItem(REFERRAL_CODE_STORAGE_KEY);
    }
  } catch (error) {
    // The form can still submit the in-memory code when storage is unavailable.
  }

  return normalizedCode;
};

export const resolveReferralCode = (search: string) => {
  const codeFromUrl = getReferralCodeFromSearch(search);
  return codeFromUrl ? storeReferralCode(codeFromUrl) : getStoredReferralCode();
};

export const clearReferralCode = () => {
  try {
    getSessionStorage()?.removeItem(REFERRAL_CODE_STORAGE_KEY);
  } catch (error) {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
};

export const getReferralAppOrigin = () => {
  if (window.ReactNativeWebView) {
    return "https://app.dataupgaming.com";
  }

  return window.location.origin;
};

export const buildShareLink = (
  code?: string | null,
  origin = getReferralAppOrigin(),
) => {
  const normalizedCode = normalizeReferralCode(code);

  if (!normalizedCode) return origin;

  const url = new URL("/signup", origin);
  url.searchParams.set("ref", normalizedCode);

  return url.toString();
};

export type ReferralShareResult = "shared" | "copied" | "cancelled";

export const shareAppLink = async (
  code?: string | null,
): Promise<ReferralShareResult> => {
  const link = buildShareLink(code);

  if (navigator.share) {
    try {
      await navigator.share({
        title: "DataUp",
        text: "Join the DataUp Family.",
        url: link,
      });
      return "shared";
    } catch (error: any) {
      if (error?.name === "AbortError") {
        return "cancelled";
      }
      // Continue to clipboard fallback when native sharing is unavailable.
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(link);
      return "copied";
    } catch (error) {
      // Continue to the legacy copy fallback.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = link;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!copied) {
    throw new Error("Unable to copy referral link");
  }

  return "copied";
};
